import Quill from 'quill';

const Module = Quill.import('core/module');

class ImageBlocker extends Module {
    constructor(quill, options) {
        super(quill, options);

        this.quill = quill;
        this.cleaningUp = false;

        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDrop = this.handleDrop.bind(this);
        this.handlePaste = this.handlePaste.bind(this);
        this.handleTextChange = this.handleTextChange.bind(this);

        this.quill.root.addEventListener('dragover', this.handleDragOver);
        this.quill.root.addEventListener('drop', this.handleDrop);
        this.quill.root.addEventListener('paste', this.handlePaste, true);
        this.quill.on('text-change', this.handleTextChange);
    }

    handleDragOver(event) {
        if (!this.hasImageFile(event.dataTransfer)) return;

        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
    }

    handleDrop(event) {
        const transfer = event.dataTransfer;
        const html = transfer && typeof transfer.getData === 'function'
            ? transfer.getData('text/html')
            : '';

        if (!this.hasImageFile(transfer) && !this.containsImageMarkup(html)) return;

        event.preventDefault();
        event.stopPropagation();
    }

    handlePaste(event) {
        if (!this.hasImageFile(event.clipboardData)) return;

        event.preventDefault();
        event.stopPropagation();
    }

    handleTextChange(delta, oldDelta, source) {
        if (source !== Quill.sources.USER || this.cleaningUp || !this.deltaContainsImage(delta)) return;

        const imageIndexes = this.getImageIndexes();
        if (!imageIndexes.length) return;

        const selection = this.quill.getSelection();
        const removedBeforeCursor = selection
            ? imageIndexes.filter(index => index < selection.index).length
            : 0;

        this.cleaningUp = true;

        imageIndexes
            .slice()
            .reverse()
            .forEach(index => {
                this.quill.deleteText(index, 1, Quill.sources.SILENT);
            });

        if (selection) {
            this.quill.setSelection(
                Math.max(selection.index - removedBeforeCursor, 0),
                Quill.sources.SILENT
            );
        }

        this.cleaningUp = false;
    }

    hasImageFile(transfer) {
        const files = Array.from((transfer && transfer.files) || []);
        const items = Array.from((transfer && transfer.items) || []);

        return files.some(file => this.isImageMime(file.type))
            || items.some(item => item.kind === 'file' && this.isImageMime(item.type));
    }

    isImageMime(type) {
        return typeof type === 'string' && type.indexOf('image/') === 0;
    }

    containsImageMarkup(html) {
        if (!html || typeof html !== 'string') return false;
        return /<img[\s>]/i.test(html) || /data:image\//i.test(html);
    }

    deltaContainsImage(delta) {
        const ops = (delta && delta.ops) || [];
        return ops.some(op => op.insert && typeof op.insert === 'object' && op.insert.image);
    }

    getImageIndexes() {
        let index = 0;
        const imageIndexes = [];
        const ops = this.quill.getContents().ops || [];

        ops.forEach(op => {
            if (typeof op.insert === 'string') {
                index += op.insert.length;
                return;
            }

            if (op.insert && typeof op.insert === 'object' && op.insert.image) {
                imageIndexes.push(index);
            }

            index += 1;
        });

        return imageIndexes;
    }
}

Quill.register('modules/imageBlocker', ImageBlocker, true);

export default ImageBlocker;
